import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Platform } from './shared/enums/platform.enum';
import { AnalyticsService, AnalyticsImplementation } from './core/analytics/analytics.service';
import { StatsigEventsService } from './core/analytics/statsig-events.service';
import { ApplicationInsightsService } from './core/analytics/application-insights.service';
import { MarketingService } from './core/analytics/marketing.service';
import { MetricService } from './core/analytics/metric.service';
import { TealiumUtagService } from './core/analytics/utag.service';
import { ConfigService } from './core/config.service';
import { DisplayOptions } from './core/display-options';
import { DisplayOptionsService } from './core/display-options.service';
import { LayoutService } from './core/layout.service';
import { SessionService } from './core/session.service';

@Component({
  standalone: false,
  selector: 'app-root',
  providers: [DisplayOptionsService, SessionService, TealiumUtagService, AnalyticsService,
    { provide: AnalyticsImplementation, useClass: MarketingService, multi: true },
    { provide: AnalyticsImplementation, useClass: MetricService, multi: true },
    { provide: AnalyticsImplementation, useClass: ApplicationInsightsService, multi: true }
  ],
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  public title = 'app';
  public disabled = false;
  private isAgent: boolean;
  public defaultLanguage = 'en';

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private window: Window;
  private displayOptions: DisplayOptions;

  constructor(
    private router: Router,
    public layoutService: LayoutService,
    private translate: TranslateService,
    private changeDetectionRef: ChangeDetectorRef,
    private configService: ConfigService,
    private displayOptionsService: DisplayOptionsService,
    private sessionService: SessionService,
    private analyticsService: AnalyticsService,
    private statsigEventsService: StatsigEventsService
  ) {
    this.displayOptionsService.initialize(configService.getDisplayOptions());
    this.sessionService.initialize(configService.getSessionData());
    this.displayOptions = this.displayOptionsService.getOptions();
    this.analyticsService.init(configService.getConfigData());
    if (this.displayOptions &&
      this.displayOptions.options &&
      this.displayOptions.options.car_langauge_override) {
      this.defaultLanguage = this.displayOptions.options.car_langauge_override;
    } else {
      this.tryToSetDefaultLanguageWithoutMemberBeingLoggedIn();
    }

    // default language
    translate.setDefaultLang(this.defaultLanguage);

    // current language
    translate.use(this.defaultLanguage);
    this.window = window;
  }

  public async ngOnInit(): Promise<any> {
    this.statsigEventsService.syncUser();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(_ => {
        this.window.scroll(0, 0);
      });

    this.layoutService.layoutStates
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(_ => this.changeDetectionRef.detectChanges());

    // Encore API (pause/resume)
    // Use Option 2 from https://ovsarchtecure.visualstudio.com/Ovs%20Core%20Components/_git/Ovs.Core.Encore?path=%2FREADME.md&version=GBmaster
    this.isAgent = this.sessionService.getSessionData().isAgent;
    if (this.displayOptions && this.displayOptions.options && this.displayOptions.options.optimizely_enabled) {
      if (this.displayOptions.options.optimizely_enabled === "true") {
        this.IncludeCdnOptimizelyScript();
      }
    }
    else {
      if (this.configService.getConfigData().optimizely.enabled) {
        this.IncludeCdnOptimizelyScript();
      }
    }
    this.IncludeEncore();

    // Google Tag Manager
    this.IncludeGtm();

    this.loadFaviconByDoD();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe?.next();
    this.ngUnsubscribe?.complete();
  }

  private IncludeCdnOptimizelyScript(): void {
    var scriptId = "cdn_script";
    var existingScript = document.getElementById(scriptId);
    if (existingScript == null) {
      var script = document.createElement("script");
      script.id = scriptId;
      script.type = "text/javascript";
      script.async = true;
      script.src = "https://cdn.optimizely.com/public/12894770201/s/globalSnippet.js";
      document.head.appendChild(script);
    }
  };

  private IncludeEncore(): void {
    if (this.isAgent) {
      var scriptId = "encore_script";
      var existingScript = document.getElementById(scriptId);
      if (existingScript == null) {
        var script = document.createElement("script");
        script.id = scriptId;
        script.type = "text/javascript";
        script.async = true;
        script.src = this.sessionService.getSessionData().getUrl("Encore") + "encore.js";
        document.body.appendChild(script);
        var link = document.createElement("link");
        link.id = "encore_styles";
        link.rel = "stylesheet";
        link.type = "text/css";
        link.href = this.sessionService.getSessionData().getUrl("Encore") + "encore.css";
        document.head.appendChild(link);
      }
    }
  }

  private IncludeGtm(): void {
    if (!this.isAgent && this.displayOptions.options !== undefined && this.displayOptions.options.web_chat_container_id) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = '(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push'
        + '({\'gtm.start\':new Date().getTime(),event:\'gtm.js\'});var f=d.getElementsByTagName(s)'
        + '[0],j=d.createElement(s),dl=l!=\'dataLayer\'?\'&l=\'+l:\'\';j.async=true;j.src='
        + '\'https://www.googletagmanager.com/gtm.js?id=\'+i+dl;f.parentNode.insertBefore(j,f);})'
        + '(window,document,\'script\',\'dataLayer\',\'' + this.displayOptions.options.web_chat_container_id + '\');';
      const head = document.getElementsByTagName('head')[0];
      head.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.innerHTML = '<iframe src="https://www.googletagmanager.com/ns.html?id=\'' + this.displayOptions.options.web_chat_container_id + '\'" height = "0" width= "0" style= "display:none;visibility:hidden" > </iframe>';
      const body = document.getElementsByTagName('body')[0];
      body.appendChild(noscript);
    }
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  private loadFaviconByDoD(): void {
    let platform = this.sessionService.getSessionData().getPlatform();
    let favicon = this.displayOptions && this.displayOptions.urls && this.displayOptions.urls.favicon;

    if (platform === Platform.SOR && favicon) {
      let link = (document.getElementById('favicon') as HTMLLinkElement);
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = this.displayOptions.urls.favicon;
    }
  }

  private tryToSetDefaultLanguageWithoutMemberBeingLoggedIn(): void {
    const dri_PartnerId = "133";
    const hgv_partnerId = "265";
    var sessionData = this.sessionService.getSessionData();
    if (sessionData.partnerId === dri_PartnerId) {
      this.defaultLanguage = "dri-en";
    } else if (sessionData.partnerId === hgv_partnerId) {
      this.defaultLanguage = "hgv-en";
    } else {
      this.defaultLanguage = "en";
    }
  }
}
